/* solura-cart-drawer.js */

class SoluraCartDrawer {
  constructor() {
    this.drawer = document.getElementById('SoluraCartDrawer');
    if (!this.drawer) return;
    
    this.container = this.drawer.querySelector('.solura-cart-drawer-container');
    this.closeButtons = this.drawer.querySelectorAll('.solura-cart-close');
    this.cartItemsWrapper = this.drawer.querySelector('.solura-cart-items-wrapper');
    this.emptyState = this.drawer.querySelector('.solura-cart-empty');
    this.cartTotalEl = this.drawer.querySelector('.solura-cart-total-price');
    this.cartCountEl = this.drawer.querySelector('.solura-cart-count');
    this.footer = this.drawer.querySelector('.solura-cart-footer');
    this.shippingBar = this.drawer.querySelector('.solura-cart-shipping-bar');
    this.isOpen = false;
    
    this.bindEvents();
    this.bindTabs();
    this.bindCartLinks();
    this.bindAddToCartForms();
    this.bindUpsellButtons();
    this.bindOfferButtons();
  }
  
  bindEvents() {
    // Fermeture via les boutons (croix, continue shopping)
    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });
    
    // Fermeture via le clic sur l'overlay sombre
    this.drawer.addEventListener('click', (e) => {
      if (e.target === this.drawer) {
        this.close();
      }
    });
    
    // Fermeture avec la touche Echap
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }
  
  bindCartLinks() {
    // Intercepter tous les liens/boutons qui pointent vers le panier
    // On cible href="/cart", les boutons de classe .solura-cart-btn, etc.
    const cartTriggers = document.querySelectorAll('a[href="/cart"], .solura-cart-btn, [data-open-cart]');
    cartTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        // Ne pas ouvrir juste parce qu'un bouton add to cart contient un lien
        if (trigger.closest('form[action*="/cart/add"]')) return;
        
        e.preventDefault();
        this.open();
      });
    });
  }

  // ==========================================
  // TABS LOGIC (STEP 2)
  // ==========================================
  switchTab(tabName) {
    const tabs = this.drawer.querySelectorAll('.cart-tab');
    const panels = this.drawer.querySelectorAll('.cart-drawer-panel');
    
    tabs.forEach(tab => {
      tab.classList.toggle('is-active', tab.getAttribute('data-tab') === tabName);
    });

    panels.forEach(panel => {
      panel.classList.toggle('is-active', panel.getAttribute('data-panel') === tabName);
    });
  }

  bindTabs() {
    const tabs = this.drawer.querySelectorAll('.cart-tab');
    
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        this.switchTab(tab.getAttribute('data-tab'));
      });
    });
  }

  // ==========================================
  // ADD TO CART AJAX (STEP 4/6)
  // ==========================================
  bindAddToCartForms() {
    // Interception au niveau du document pour inclure tous les formulaires (même injectés dynamiquement)
    // Utilisation de { capture: true } pour attraper l'événement AVANT les scripts natifs Shopify
    document.addEventListener('submit', async (e) => {
      const form = e.target;
      
      // On cible spécifiquement les formulaires d'ajout natifs Shopify
      if (form.matches('form[action*="/cart/add"]')) {
        e.preventDefault();
        e.stopImmediatePropagation(); // Force l'arrêt complet des autres scripts (comme le quick-add natif)
        
        const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
        if (!submitBtn || submitBtn.disabled) return; // Évite les doubles clics
        
        const originalText = submitBtn.innerHTML;
        const btnWidth = submitBtn.offsetWidth;
        
        // 1. Loading state (Bouton disabled + Spinner)
        submitBtn.disabled = true;
        submitBtn.style.width = `${btnWidth}px`; // Évite un saut de layout
        submitBtn.innerHTML = '<span class="solura-spinner"></span>';
        
        // Nettoyage erreur précédente
        const oldError = form.querySelector('.solura-add-error');
        if (oldError) oldError.remove();

        try {
          // 2. Fetch /cart/add.js
          const formData = new FormData(form);
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData,
            headers: { 'Accept': 'application/json' }
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.description || 'Error adding to cart');
          }
          
          // 3. Succès ! 
          if (this.isOpen) {
            // Le drawer est déjà ouvert (ex: upsell ou clic très rapide) -> On refresh smooth
            await this.fetchCart();
            if (this.cartItemsWrapper) {
              this.cartItemsWrapper.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll haut
            }
          } else {
            // Ouvrir le drawer (l'ouverture lance fetchCart automatiquement)
            this.open();
          }

        } catch (error) {
          console.error(error);
          // 4. Affichage Erreur (Stock insuffisant, variante manquante...)
          const errorMsg = document.createElement('p');
          errorMsg.className = 'solura-add-error';
          errorMsg.style.color = 'var(--orange-glow, #FF5A1F)';
          errorMsg.style.fontSize = '12px';
          errorMsg.style.marginTop = '12px';
          errorMsg.style.fontFamily = 'var(--font-sans, sans-serif)';
          errorMsg.textContent = error.message;
          form.appendChild(errorMsg);
        } finally {
          // 5. Restaurer le bouton
          submitBtn.disabled = false;
          submitBtn.innerHTML = originalText;
          submitBtn.style.width = '';
        }
      }
    }, { capture: true });
  }
  
  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    
    // Rendre l'overlay visible
    this.drawer.classList.add('is-active');
    
    // Verrouiller le scroll du body sans effet de saut
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    // Fetch and render actual cart content
    this.fetchCart();
  }
  
  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    
    // Cacher l'overlay
    this.drawer.classList.remove('is-active');
    
    // Rétablir le scroll après la fin de la transition CSS (0.4s)
    setTimeout(() => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }, 400);
  }

  // ==========================================
  // AJAX CART LOGIC (STEP 3)
  // ==========================================

  async fetchCart() {
    this.drawer.classList.add('is-loading');
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.renderCart(cart);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      this.drawer.classList.remove('is-loading');
    }
  }

  renderCart(cart) {
    // 1. Update counts
    if(this.cartCountEl) this.cartCountEl.textContent = `(${cart.item_count})`;
    const tabCount = this.drawer.querySelector('.solura-cart-count-tab');
    if (tabCount) tabCount.textContent = cart.item_count;
    
    // Mettre à jour les bulles de compteur cart natives de Shopify (Dawn etc)
    document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach(el => {
      el.textContent = cart.item_count;
    });

    // 2. Empty state vs Items
    if (cart.item_count === 0) {
      this.emptyState.style.display = 'flex';
      this.cartItemsWrapper.style.display = 'none';
      this.footer.style.display = 'none';
      if(this.shippingBar) this.shippingBar.style.display = 'none';
      return;
    }

    this.emptyState.style.display = 'none';
    this.cartItemsWrapper.style.display = 'block';
    this.footer.style.display = 'block';
    if(this.shippingBar) this.shippingBar.style.display = 'flex';

    // Update Upsells visibility (in Cart Tab)
    const upsellItems = this.drawer.querySelectorAll('.solura-upsell-item');
    const cartProductIds = cart.items.map(item => item.product_id.toString());
    
    let visibleUpsells = 0;
    upsellItems.forEach(upsell => {
      const pId = upsell.getAttribute('data-product-id');
      if (cartProductIds.includes(pId)) {
        upsell.style.display = 'none';
      } else {
        upsell.style.display = 'flex';
        visibleUpsells++;
      }
    });

    const upsellsWrapper = this.drawer.querySelector('.solura-cart-upsells-wrapper');
    if (upsellsWrapper) {
      upsellsWrapper.style.display = visibleUpsells > 0 ? 'block' : 'none';
    }

    // Update Offers visibility (in Offers Tab)
    const offerItems = this.drawer.querySelectorAll('.solura-offer-item');
    let visibleOffers = 0;
    offerItems.forEach(offer => {
      const pId = offer.getAttribute('data-product-id');
      if (!pId) {
        offer.style.display = 'grid';
        visibleOffers++;
        return;
      }

      const hideIfInCart = offer.getAttribute('data-hide-if-in-cart') === 'true';
      const inCart = cartProductIds.includes(pId);
      
      const addBtn = offer.querySelector('.solura-offer-add-btn');
      const addedBtn = offer.querySelector('.solura-offer-added-btn');

      if (inCart) {
        if (hideIfInCart) {
          offer.style.display = 'none';
        } else {
          offer.style.display = 'grid';
          visibleOffers++;
          if (addBtn) addBtn.style.display = 'none';
          if (addedBtn) addedBtn.style.display = 'flex';
        }
      } else {
        offer.style.display = 'grid';
        visibleOffers++;
        if (addBtn) addBtn.style.display = 'flex';
        if (addedBtn) addedBtn.style.display = 'none';
      }
    });

    // Handle tab notification dot
    const offersTabDot = this.drawer.querySelector('.cart-tab[data-tab="offers"] .cart-tab-dot');
    if (offersTabDot) {
      offersTabDot.style.display = visibleOffers > 0 ? 'inline-block' : 'none';
    }

    // 3. Update total
    if(this.cartTotalEl) this.cartTotalEl.innerHTML = this.formatMoney(cart.total_price);

    // 4. Render items
    let html = '';
    cart.items.forEach(item => {
      const optionsHtml = item.variant_options 
        ? item.variant_options.filter(opt => opt !== 'Default Title').map(opt => `<span class="solura-item-option">${opt}</span>`).join('') 
        : '';
        
      html += `
        <div class="solura-cart-item" data-key="${item.key}">
          <div class="solura-item-image">
            ${item.image ? `<img src="${item.image}" alt="${item.product_title}">` : ''}
          </div>
          <div class="solura-item-details">
            <div class="solura-item-header">
              <a href="${item.url}" class="solura-item-title">${item.product_title}</a>
              <button class="solura-item-remove" data-key="${item.key}" aria-label="Remove">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            ${optionsHtml ? `<div class="solura-item-options">${optionsHtml}</div>` : ''}
            <div class="solura-item-bottom">
              <div class="solura-item-qty">
                <button type="button" class="solura-qty-btn solura-qty-minus" data-key="${item.key}">-</button>
                <input type="number" class="solura-qty-input" value="${item.quantity}" min="0" readonly>
                <button type="button" class="solura-qty-btn solura-qty-plus" data-key="${item.key}">+</button>
              </div>
              <div class="solura-item-price">${this.formatMoney(item.final_line_price)}</div>
            </div>
          </div>
        </div>
      `;
    });

    this.cartItemsWrapper.innerHTML = html;
    this.bindItemEvents();
  }

  bindItemEvents() {
    const minusBtns = this.cartItemsWrapper.querySelectorAll('.solura-qty-minus');
    const plusBtns = this.cartItemsWrapper.querySelectorAll('.solura-qty-plus');
    const removeBtns = this.cartItemsWrapper.querySelectorAll('.solura-item-remove');

    minusBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.getAttribute('data-key');
        const input = btn.nextElementSibling;
        const newQty = parseInt(input.value) - 1;
        this.changeQuantity(key, newQty);
      });
    });

    plusBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.getAttribute('data-key');
        const input = btn.previousElementSibling;
        const newQty = parseInt(input.value) + 1;
        this.changeQuantity(key, newQty);
      });
    });

    removeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.getAttribute('data-key');
        this.changeQuantity(key, 0); // Remove = quantity 0
      });
    });
  }

  // ==========================================
  // UPSELLS (STEP 5)
  // ==========================================
  bindUpsellButtons() {
    const upsellBtns = this.drawer.querySelectorAll('.solura-upsell-add-btn:not(.is-disabled)');
    upsellBtns.forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        if (btn.disabled) return;
        
        const variantId = btn.getAttribute('data-variant-id');
        const originalText = btn.innerHTML;
        const btnWidth = btn.offsetWidth;
        
        // Loading state
        btn.disabled = true;
        btn.style.width = `${btnWidth}px`;
        btn.innerHTML = '<span class="solura-spinner" style="width:12px;height:12px;border-width:2px;border-color:currentColor;border-bottom-color:transparent;"></span>';
        
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 })
          });
          
          if (!response.ok) {
            throw new Error('Failed to add upsell');
          }
          
          // Refresh drawer content smoothly
          await this.fetchCart();
          if (this.cartItemsWrapper) {
            this.cartItemsWrapper.scrollTo({ top: this.cartItemsWrapper.scrollHeight, behavior: 'smooth' });
          }
          
        } catch (error) {
          console.error(error);
          btn.textContent = 'Error';
        } finally {
          btn.disabled = false;
          btn.innerHTML = originalText;
          btn.style.width = '';
        }
      });
    });
  }

  // ==========================================
  // OFFERS BUTTONS (STEP 3)
  // ==========================================
  bindOfferButtons() {
    const offerBtns = this.drawer.querySelectorAll('.solura-offer-add-btn:not(.is-disabled)');
    offerBtns.forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        if (btn.disabled) return;
        
        const variantId = btn.getAttribute('data-variant-id');
        const afterAdd = btn.getAttribute('data-after-add'); // 'stay_on_offers' or 'switch_to_cart'
        const originalText = btn.innerHTML;
        const btnWidth = btn.offsetWidth;
        
        // Loading state
        btn.disabled = true;
        btn.style.width = `${btnWidth}px`;
        btn.innerHTML = '<span class="solura-spinner" style="width:12px;height:12px;border-width:2px;border-color:currentColor;border-bottom-color:transparent;"></span>';
        
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 })
          });
          
          if (!response.ok) {
            throw new Error('Failed to add offer');
          }
          
          await this.fetchCart();
          
          // Switch to cart if configured
          if (afterAdd === 'switch_to_cart') {
            this.switchTab('cart');
            if (this.cartItemsWrapper) {
              this.cartItemsWrapper.scrollTo({ top: this.cartItemsWrapper.scrollHeight, behavior: 'smooth' });
            }
          }
          
        } catch (error) {
          console.error(error);
          btn.textContent = 'Error';
        } finally {
          btn.disabled = false;
          btn.innerHTML = originalText;
          btn.style.width = '';
        }
      });
    });
  }

  async changeQuantity(key, quantity) {
    this.drawer.classList.add('is-loading');
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      const cart = await response.json();
      this.renderCart(cart);
    } catch (error) {
      console.error('Error updating quantity:', error);
    } finally {
      this.drawer.classList.remove('is-loading');
    }
  }

  formatMoney(cents) {
    // Shopify formatting (basic fallback if window.Shopify.formatMoney is missing)
    return '$' + (cents / 100).toFixed(2);
  }
}

// Initialisation globale
document.addEventListener('DOMContentLoaded', () => {
  window.soluraCart = new SoluraCartDrawer();
});
